import React, { useState, useCallback, useEffect } from 'react';
import { Checkbox, Input } from 'antd';
import { RightOutlined, LeftOutlined, SearchOutlined } from '@ant-design/icons';
import { mdlKeyTitlePair } from '../../models/domain/value-objects/KeyTitlePair';
import { useTranslation } from 'react-i18next';

type Key = string;
type TransferDirection = 'left' | 'right';

interface TransferItem {
    key: Key;
    title: string;
    rawTitle?: string;
    description: string;
    isParent: boolean;
    groupKey?: Key;
    children?: Key[];
}

interface CustomTransferProps {
    dataSource: mdlKeyTitlePair[];
    selectedKeys: Key[] | undefined;
    onChange: (keys: Key[], direction: TransferDirection, moveKeys: Key[]) => void;
    parentChangeStatus?: boolean;
    parentChangeFunction?: Function;
}

const CustomTransfer: React.FC<CustomTransferProps> = ({ 
    dataSource = [], 
    selectedKeys = [],
    onChange ,
    parentChangeStatus = false,
    parentChangeFunction = undefined
}) => {
    const { t } = useTranslation();
    const [targetKeys, setTargetKeys] = useState<Key[]>([]);
    const [localSelectedKeys, setLocalSelectedKeys] = useState<Key[]>([]);
    const [transferData, setTransferData] = useState<TransferItem[]>([]);
    const [leftSearchText, setLeftSearchText] = useState('');
    const [rightSearchText, setRightSearchText] = useState('');

    const getChildrenKeys = useCallback((parentKey: Key): Key[] => {
        return transferData
            .filter(item => !item.isParent && item.groupKey === parentKey)
            .map(item => item.key);
    }, [transferData]);

    const processData = useCallback((items: mdlKeyTitlePair[]): TransferItem[] => {
        const result: TransferItem[] = [];
        
        if (!items || !Array.isArray(items)) return result;

        items.map(x => ({...x,["key"] : x.key?.replace("1_","null_null_")})).forEach(group => {
            if (!group.key) return;

            const childKeys: Key[] = [];
            const children = group.children?.map(child => {
                if (!child.key) return null;

                const childKey = child.parent?.key ? `${child.parent.key}_${child.key}` : child.key;
                
                childKeys.push(childKey);
                return {
                    key: childKey,
                    title: child.title || '',
                    rawTitle: child.title || '',
                    description: child.title || '',
                    isParent: false,
                    groupKey: group.key
                };
            }).filter(Boolean) as TransferItem[];

            result.push({
                key: group.key,
                title: group.title || '',
                description: group.title || '',
                isParent: true,
                children: childKeys
            });

            if (children) {
                result.push(...children);
            }
        });
        
        return result;
    }, []);

    useEffect(() => {
        if (Array.isArray(dataSource) && dataSource.length > 0) {
            const processedData = processData(dataSource);
            setTransferData(processedData);
        }
        if(parentChangeStatus && parentChangeFunction){
            setLocalSelectedKeys([]);
            setTargetKeys([]);
            parentChangeFunction();
        }
    }, [dataSource, processData]);

    const getParentSelectionsFromChildren = useCallback((childKeys: Key[]) => {
        const parentSelections: Key[] = [];
        
        transferData.forEach(item => {
            if (item.isParent && item.children) {
                const parentChildKeys = getChildrenKeys(item.key);
                
                const allChildrenSelected = parentChildKeys.length > 0 && 
                    parentChildKeys.every(childKey => childKeys.includes(childKey));
                
                if (allChildrenSelected) {
                    parentSelections.push(item.key);
                }
            }
        });

        return parentSelections;
    }, [transferData, getChildrenKeys]);

    useEffect(() => {
        let initialTargetKeys = (selectedKeys || []).map( x=> x.replace("1_","null_null_"));
        if(initialTargetKeys != targetKeys && initialTargetKeys.length > 0){
            const nullCategoryItems = initialTargetKeys.filter( x=> x.includes("null_"));
            if(nullCategoryItems && nullCategoryItems.length == initialTargetKeys.length && !initialTargetKeys.find( x=> x == "null")){
                initialTargetKeys = [...initialTargetKeys,...[("null")]];
            }
            setTargetKeys(initialTargetKeys);
            const parentSelections = getParentSelectionsFromChildren(initialTargetKeys);
            setLocalSelectedKeys([...new Set([...initialTargetKeys, ...parentSelections])]);
        }
    }, [selectedKeys, getParentSelectionsFromChildren]);

    const getVisibleChildrenKeys = useCallback((parentKey: Key, searchText: string, isTargetList: boolean): Key[] => {
        const allChildren = transferData
            .filter(item => item.groupKey === parentKey)
            .map(item => item.key);

        if (!searchText) return allChildren;

        return allChildren.filter(key => {
            const item = transferData.find(i => i.key === key);
            const isVisible = item?.title.toLowerCase().includes(searchText.toLowerCase());
            const isInCorrectList = isTargetList ? targetKeys.includes(key) : !targetKeys.includes(key);
            return isVisible && isInCorrectList;
        });
    }, [transferData, targetKeys]);

    const handleSelectChange = useCallback((key: Key, checked: boolean, searchText: string, isTargetList: boolean) => {
        const item = transferData.find(i => i.key === key);
        if (!item) return;

        const newSelectedKeys = [...localSelectedKeys];

        if (item.isParent) {
            const visibleChildrenKeys = getVisibleChildrenKeys(key, searchText, isTargetList);
            if (checked) {
                if (!newSelectedKeys.includes(key)) {
                    newSelectedKeys.push(key);
                }
                visibleChildrenKeys.forEach(childKey => {
                    if (!newSelectedKeys.includes(childKey)) {
                        newSelectedKeys.push(childKey);
                    }
                });
            } else {
                const indexToRemove = newSelectedKeys.indexOf(key);
                if (indexToRemove > -1) {
                    newSelectedKeys.splice(indexToRemove, 1);
                }
                visibleChildrenKeys.forEach(childKey => {
                    const childIndex = newSelectedKeys.indexOf(childKey);
                    if (childIndex > -1) {
                        newSelectedKeys.splice(childIndex, 1);
                    }
                });
            }
        } else {
            if (checked) {
                if (!newSelectedKeys.includes(key)) {
                    newSelectedKeys.push(key);
                }
            } else {
                const indexToRemove = newSelectedKeys.indexOf(key);
                if (indexToRemove > -1) {
                    newSelectedKeys.splice(indexToRemove, 1);
                }
            }

            if (item.groupKey) {
                const allChildren = getVisibleChildrenKeys(item.groupKey, searchText, isTargetList);
                const allSelected = allChildren.every(childKey => newSelectedKeys.includes(childKey));
                
                const parentIndex = newSelectedKeys.indexOf(item.groupKey);
                if (allSelected) {
                    if (parentIndex === -1) {
                        newSelectedKeys.push(item.groupKey);
                    }
                } else {
                    if (parentIndex > -1) {
                        newSelectedKeys.splice(parentIndex, 1);
                    }
                }
            }
        }

        setLocalSelectedKeys(newSelectedKeys);
    }, [transferData, getVisibleChildrenKeys, localSelectedKeys]);

    const handleTransfer = (direction: TransferDirection) => {
        const moveKeys = localSelectedKeys;
        let newTargetKeys = [...targetKeys];

        if (direction === 'right') {
            newTargetKeys = [...new Set([...newTargetKeys, ...moveKeys])];
        } else {
            newTargetKeys = newTargetKeys.filter(key => !moveKeys.includes(key));
        }
        newTargetKeys = newTargetKeys.filter( x => !x.includes('undefined'));
        setTargetKeys(newTargetKeys);
        onChange(newTargetKeys, direction, moveKeys);
        setLocalSelectedKeys([]);
        setLeftSearchText("")
        setRightSearchText("")
    };

    const getFilteredItems = useCallback((isTargetList: boolean) => {
        const searchText = isTargetList ? rightSearchText : leftSearchText;
        
        const relevantParents = transferData.filter(item => item.isParent).filter(parent => {
            const hasChildrenInList = parent.children?.some(childKey => 
                isTargetList ? targetKeys.includes(childKey) : true
            );
            return hasChildrenInList;
        });

        const result: TransferItem[] = [];
        
        relevantParents.forEach(parent => {
            if (!searchText || parent.title.toLowerCase().includes(searchText.toLowerCase())) {
                result.push(parent);
            }
            
            const children = transferData.filter(item => 
                !item.isParent && 
                item.groupKey === parent.key
            );

            children.forEach(child => {
                const isChildInTargetList = targetKeys.includes(child.key);
                const shouldShowInCurrentList = isTargetList ? isChildInTargetList : true;
                
                if (shouldShowInCurrentList && 
                    (!searchText || child.title.toLowerCase().includes(searchText.toLowerCase()))) {
                    result.push(child);
                }
            });
        });

        return result;
    }, [transferData, targetKeys, leftSearchText, rightSearchText]);

    const renderListItem = (item: TransferItem, isSelected: boolean, isDisabled: boolean, searchText: string, isTargetList: boolean) => {
        let displayTitle = item.title;
        if (!item.isParent && searchText && item.groupKey) {
            const parentItem = transferData.find(i => i.key === item.groupKey);
            if (parentItem) {
                displayTitle = `${item.title} (${parentItem.title})`;
            }
        }

        return (
            <div
                key={item.key}
                className={`transfer-list-item ${item.isParent ? 'parent' : 'child'} ${isDisabled ? 'disabled' : ''}`}
            >
                <Checkbox
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={(e) => handleSelectChange(item.key, e.target.checked, searchText, isTargetList)}
                />
                <span className={`title ${!item.isParent && searchText ? 'search-result' : ''}`}>
                    {displayTitle}
                </span>
            </div>
        );
    };

    const renderList = (isTargetList: boolean = false) => {
        const filteredItems = getFilteredItems(isTargetList);
        
        return (
            <div className="transfer-list-container">
                <Input
                    placeholder={t("common.search")}
                    prefix={<SearchOutlined />}
                    value={(isTargetList ? rightSearchText : leftSearchText)}
                    onChange={(e) => isTargetList ? setRightSearchText(e.target.value) : setLeftSearchText(e.target.value)}
                    className="transfer-search"
                />
                <div className="transfer-list">
                    {filteredItems.map(item => {
                        const isDisabled = !isTargetList && targetKeys.includes(item.key);
                        const isSelected = localSelectedKeys.includes(item.key);
                        return renderListItem(item, isSelected, isDisabled, (isTargetList ? rightSearchText : leftSearchText), isTargetList);
                    })}
                    {filteredItems.length === 0 && (
                        <div className="empty-list">
                            {t("common.emptyList")}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="custom-transfer-container">
            {renderList(false)}
            <div className="transfer-buttons">
                <button
                    className="transfer-button"
                    onClick={() => handleTransfer('right')}
                    disabled={localSelectedKeys.length === 0}
                >
                    <RightOutlined />
                </button>
                <button
                    className="transfer-button"
                    onClick={() => handleTransfer('left')}
                    disabled={localSelectedKeys.length === 0}
                >
                    <LeftOutlined />
                </button>
            </div>
            {renderList(true)}
        </div>
    );
};

export default CustomTransfer;